import { getAccessToken } from "../suite/auth";
import { candidateForgeApiBases } from "./generate";
import type { FinishPresetId } from "./takesStore";

/** AccessHub Features id — denied by default, Allow on the user page. */
export const FINISH_TOOL_ID = "forge-music-production-finish";

export function productionFinishAllowed(caps: Record<string, unknown> | null | undefined): boolean {
  if (!caps) return false;
  const detail = caps.tools_detail;
  if (detail && typeof detail === "object") {
    const row = (detail as Record<string, { allowed?: boolean }>)[FINISH_TOOL_ID];
    if (row && typeof row.allowed === "boolean") return row.allowed;
  }
  const tools = caps.tools;
  if (Array.isArray(tools)) {
    return tools.map(String).includes(FINISH_TOOL_ID);
  }
  return false;
}

export type FinishPreset = {
  id: FinishPresetId;
  label: string;
  blurb: string;
};

export const FINISH_PRESETS: FinishPreset[] = [
  { id: "spotify", label: "Spotify", blurb: "Loud Mixea-like master (−9 LUFS)" },
  { id: "apple", label: "Apple Music", blurb: "Glued master (−14 LUFS)" },
  { id: "youtube", label: "YouTube", blurb: "Loud master (−11 LUFS)" },
];

export function loadFinishPreset(): FinishPresetId {
  try {
    const v = localStorage.getItem("musicforge.finishPreset.v1");
    if (v === "spotify" || v === "apple" || v === "youtube") return v;
  } catch {
    /* */
  }
  return "spotify";
}

export function saveFinishPreset(id: FinishPresetId): void {
  try {
    localStorage.setItem("musicforge.finishPreset.v1", id);
  } catch {
    /* */
  }
}

type JobSnap = {
  id: string;
  status?: string;
  error?: string;
  code?: string;
  queuedAhead?: number;
  lufs?: number;
  truePeakDb?: number;
  preset?: FinishPresetId;
};

export type FinishProgress = {
  phase: "queued" | "running" | "fetch";
  message: string;
};

export type FinishOk = {
  blob: Blob;
  contentType: string;
  preset: FinishPresetId;
  lufs?: number;
  truePeakDb?: number;
};

function authHeaders(): Record<string, string> {
  const tok = getAccessToken();
  return tok ? { Authorization: `Bearer ${tok}` } : {};
}

async function parseJob(res: Response): Promise<JobSnap | null> {
  const ctype = res.headers.get("content-type") || "";
  if (!ctype.includes("json")) return null;
  try {
    return (await res.json()) as JobSnap;
  } catch {
    return null;
  }
}

async function startJob(
  base: string,
  audio: Blob,
  preset: FinishPresetId,
  signal?: AbortSignal,
): Promise<{ job?: JobSnap; error?: string }> {
  const res = await fetch(`${base}/api/musicforge/finish/jobs?preset=${encodeURIComponent(preset)}`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": audio.type || "application/octet-stream",
      ...authHeaders(),
    },
    body: audio,
    signal,
  });
  const job = await parseJob(res);
  if (!job?.id) {
    const raw = job ? JSON.stringify(job) : await res.text().catch(() => "");
    return { error: raw.slice(0, 280) || `Production finish failed (${res.status})` };
  }
  if (job.status === "error") {
    return { error: job.error || "Production finish failed", job };
  }
  return { job };
}

async function pollJob(
  base: string,
  id: string,
  signal: AbortSignal | undefined,
  onProgress?: (p: FinishProgress) => void,
): Promise<JobSnap> {
  const deadline = Date.now() + 12 * 60_000;
  while (Date.now() < deadline) {
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
    const res = await fetch(`${base}/api/musicforge/finish/jobs/${encodeURIComponent(id)}`, {
      headers: { Accept: "application/json", ...authHeaders() },
      signal,
    });
    const job = await parseJob(res);
    if (!job) throw new Error(`Finish poll failed (${res.status})`);
    if (job.status === "ok" || job.status === "error") return job;
    const ahead = Number(job.queuedAhead || 0);
    onProgress?.({
      phase: job.status === "queued" ? "queued" : "running",
      message:
        ahead > 0
          ? `Production finish queued (${ahead} ahead)…`
          : "Production finish — Spotify-level master…",
    });
    await new Promise((r) => setTimeout(r, 1_000));
  }
  throw new Error("Production finish timed out after 12 minutes.");
}

async function fetchJobAudio(
  base: string,
  id: string,
  signal?: AbortSignal,
): Promise<Blob> {
  const res = await fetch(`${base}/api/musicforge/finish/jobs/${encodeURIComponent(id)}/audio`, {
    headers: { Accept: "audio/wav, application/json", ...authHeaders() },
    signal,
  });
  const ctype = res.headers.get("content-type") || "";
  if (!res.ok) {
    const raw = await res.text().catch(() => "");
    throw new Error(raw.slice(0, 280) || `Finish audio failed (${res.status})`);
  }
  if (ctype.includes("json")) {
    const j = (await res.json()) as { error?: string };
    throw new Error(j.error || "Finished audio not ready");
  }
  return new Blob([await res.arrayBuffer()], { type: ctype || "audio/wav" });
}

export async function productionFinish(opts: {
  audio: Blob;
  preset: FinishPresetId;
  signal?: AbortSignal;
  onProgress?: (p: FinishProgress) => void;
}): Promise<{ ok: true; result: FinishOk } | { ok: false; error: string }> {
  const bases = candidateForgeApiBases();
  let lastErr = "Production finish failed";
  for (const base of bases) {
    try {
      const started = await startJob(base, opts.audio, opts.preset, opts.signal);
      if (started.error) {
        lastErr = started.error;
        if (/failed to fetch|networkerror|load failed|econnrefused/i.test(started.error)) continue;
        return { ok: false, error: lastErr };
      }
      const job = started.job!;
      opts.onProgress?.({
        phase: job.status === "queued" ? "queued" : "running",
        message: "Production finish — mastering for upload…",
      });
      const done = await pollJob(base, job.id, opts.signal, opts.onProgress);
      if (done.status === "error") {
        return { ok: false, error: done.error || "Production finish failed" };
      }
      opts.onProgress?.({ phase: "fetch", message: "Fetching finished WAV…" });
      const blob = await fetchJobAudio(base, job.id, opts.signal);
      return {
        ok: true,
        result: {
          blob,
          contentType: blob.type || "audio/wav",
          preset: done.preset || opts.preset,
          lufs: done.lufs,
          truePeakDb: done.truePeakDb,
        },
      };
    } catch (e) {
      if (e instanceof DOMException && e.name === "AbortError") throw e;
      lastErr = e instanceof Error ? e.message : String(e);
      if (/failed to fetch|networkerror|load failed|econnrefused/i.test(lastErr)) continue;
      return { ok: false, error: lastErr };
    }
  }
  return { ok: false, error: lastErr };
}
